import React from 'react';
import { FreightItem } from '../types';
import { InfoTooltip } from './InfoTooltip';
import { useLanguage } from '../contexts/LanguageContext';

interface FreightFormProps {
  items: FreightItem[];
  setItems: React.Dispatch<React.SetStateAction<FreightItem[]>>;
}

export const FreightForm: React.FC<FreightFormProps> = ({ items, setItems }) => {
  const { t } = useLanguage();

  const updateItem = (id: string, field: keyof FreightItem, value: any) => {
    setItems(prev => prev.map(item => {
      if (item.id === id) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  const addItem = () => {
    const newItem: FreightItem = {
      id: crypto.randomUUID(),
      length: 48,
      width: 40,
      height: 48,
      weight: 500,
      quantity: 1,
      isStackable: false,
      description: ''
    };
    setItems([...items, newItem]);
  };

  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-slate-800 flex items-center">
          {t('cargoDetails')}
          <InfoTooltip content="Include pallet dimensions. If items overhang the pallet, measure the widest point." />
        </h2>
        <button 
          onClick={addItem}
          className="no-print text-sm font-semibold bg-indigo-50 text-indigo-600 hover:bg-indigo-100 px-4 py-2 rounded-lg flex items-center gap-2 transition-all border border-indigo-200"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
          </svg>
          {t('addPallet')}
        </button>
      </div>

      <div className="space-y-4">
        {items.map((item, index) => (
          <div key={item.id} className="bg-white p-5 rounded-xl shadow-md border border-slate-100 relative group transition-all hover:shadow-lg">
             {/* Number Badge */}
             <div className="absolute -left-3 top-5 bg-slate-900 text-white text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full shadow z-10">
                {index + 1}
             </div>
             
             <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-start">
                {/* Dimensions */}
                <div className="md:col-span-5 grid grid-cols-3 gap-3">
                    <div className="flex flex-col">
                        <label className="text-xs font-semibold text-slate-500 mb-1.5 uppercase">{t('length')}</label>
                        <div className="relative">
                            <input 
                                type="number" min="1"
                                value={item.length}
                                onChange={(e) => updateItem(item.id, 'length', parseFloat(e.target.value) || 0)}
                                className="w-full pl-3 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                            />
                            <span className="absolute right-3 top-2.5 text-xs text-slate-400">in</span>
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <label className="text-xs font-semibold text-slate-500 mb-1.5 uppercase">{t('width')}</label>
                        <div className="relative">
                            <input 
                                type="number" min="1"
                                value={item.width}
                                onChange={(e) => updateItem(item.id, 'width', parseFloat(e.target.value) || 0)}
                                className="w-full pl-3 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                            />
                            <span className="absolute right-3 top-2.5 text-xs text-slate-400">in</span>
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <label className="text-xs font-semibold text-slate-500 mb-1.5 uppercase">{t('height')}</label>
                        <div className="relative">
                            <input 
                                type="number" min="1"
                                value={item.height}
                                onChange={(e) => updateItem(item.id, 'height', parseFloat(e.target.value) || 0)}
                                className="w-full pl-3 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                            />
                            <span className="absolute right-3 top-2.5 text-xs text-slate-400">in</span>
                        </div>
                    </div>
                </div>

                {/* Weight & Qty */}
                <div className="md:col-span-3 grid grid-cols-2 gap-3">
                    <div className="flex flex-col">
                        <label className="text-xs font-semibold text-slate-500 mb-1.5 uppercase">{t('weight')}</label>
                        <div className="relative">
                            <input 
                                type="number" min="1"
                                value={item.weight}
                                onChange={(e) => updateItem(item.id, 'weight', parseFloat(e.target.value) || 0)}
                                className="w-full pl-3 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                            />
                            <span className="absolute right-3 top-2.5 text-xs text-slate-400">lb</span>
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <label className="text-xs font-semibold text-slate-500 mb-1.5 uppercase">{t('qty')}</label>
                        <input 
                            type="number" min="1"
                            value={item.quantity}
                            onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                            className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                        />
                    </div>
                </div>

                {/* Description & Options */}
                <div className="md:col-span-4 flex flex-col gap-3">
                    <div>
                        <label className="text-xs font-semibold text-slate-500 mb-1.5 uppercase">{t('descOptional')}</label>
                        <input 
                            type="text" 
                            placeholder="..."
                            value={item.description}
                            onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                            className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                        />
                    </div>
                    
                    <div className="flex items-center justify-between mt-1">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                            <div className="relative">
                                <input 
                                    type="checkbox" 
                                    className="peer sr-only"
                                    checked={item.isStackable}
                                    onChange={(e) => updateItem(item.id, 'isStackable', e.target.checked)}
                                />
                                <div className="w-9 h-5 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                            </div>
                            <span className="text-sm text-slate-600 font-medium">{t('stackable')}</span>
                        </label>

                         {items.length > 1 && (
                            <button 
                                onClick={() => removeItem(item.id)}
                                className="text-slate-400 hover:text-red-500 transition-colors p-1"
                                title={t('remove')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
             </div>
          </div>
        ))}
      </div>
    </div>
  );
};